import { Col } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { DeviceAction } from '@portal/actions';

import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedForm from '@portal/components/AdvancedForm/AdvancedForm';
import SmsCode from '@portal/components/SmsCode/SmsCode';

import Loading from '@portal/components/Loading/Loading';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { TokenMethod } from '@portal/enum/device';
import { TaxIdentifierType } from '@portal/enum/onboardingType';
import { useNavigation } from '@portal/hooks/useNavigation';
import useReduxState from '@portal/hooks/useReduxState';
import {
  AccountStatus,
  AccountTaxIdentifierType,
} from '@portal/models/enumerators';
import { validateDevice } from '@portal/repositories/securitySdkConfig';
import { translate } from '@portal/services/i18n';
import NavigationService from '@portal/services/navigation';

interface LocationState {
  method?: TokenMethod;
}

const RequestSms = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const location = useLocation<LocationState>();
  const method = location.state?.method || TokenMethod.SMS;

  const [disableButton, setDisableButton] = useState(true);
  const [code, setCode] = useState('');

  const {
    user: {
      me: { id, account, mustChangePassword, mustChangePin },
    },
    loading: { amount: isLoading },
    auth: { validateTokenStatus },
    theme: { currentTheme },
  } = useReduxState();

  useEffect(() => {
    if (validateTokenStatus) {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateTokenStatus]);

  useEffect(() => {
    if (account.status) {
      const typeAccount =
        account.type === AccountTaxIdentifierType.PERSON
          ? TaxIdentifierType.PF
          : TaxIdentifierType.PJ;
      if (account.status === AccountStatus.PENDING_ACCOUNT_DATA_RETRY) {
        navigate('/abertura-de-conta', {
          state: {
            isRetry: true,
            accountType: typeAccount,
          },
        });
      } else {
        const newTab = NavigationService.getOnboardingTab(account.status);

        if (newTab) {
          navigate('/abertura-de-conta', {
            state: {
              tab: newTab,
              accountType: typeAccount,
            },
          });
        }
      }
    }
  }, [account.status, account.type, dispatch, history]);

  useEffect(() => {
    if (mustChangePassword) {
      navigate('/trocar-credenciais/senha');
    } else if (mustChangePin) {
      navigate('/trocar-credenciais/pin');
    }
  }, [navigate, mustChangePassword, mustChangePin]);

  useEffect(() => {
    if (code) {
      setDisableButton(false);
    } else {
      setDisableButton(true);
    }
  }, [code]);

  const handleResend = (selectedMethod?: TokenMethod) => {
    dispatch(DeviceAction.registerDevice(id ?? '', selectedMethod || method));
  };

  const handleValidateSMS = () => {
    validateDevice(code);
  };

  return (
    <>
      {isLoading > 0 && <Loading />}
      <div
        className="request-sms"
        style={{ background: currentTheme.background }}
      >
        <div className="request-sms__form">
          <div className="request-sms__form__icon-lock">
            <img
              src="assets/svg/lock.svg"
              alt={translate('PAGES.AUTH.REQUEST_SMS.TITLE')}
            />
          </div>
          <ThemedText variant="h2" className="request-sms__form__header">
            {translate('PAGES.AUTH.REQUEST_SMS.TITLE')}
          </ThemedText>
          <ThemedText variant="body" className="request-sms__form__message">
            {translate(
              method === TokenMethod.WHATSAPP
                ? 'PAGES.AUTH.REQUEST_SMS.TEXT_WHATSAPP'
                : 'PAGES.AUTH.REQUEST_SMS.TEXT_SMS'
            )}
          </ThemedText>
          <AdvancedForm
            onSubmit={disableButton ? undefined : handleValidateSMS}
          >
            <div className="request-sms__form__container">
              <SmsCode
                className="change-credentials__form__inputs--focused"
                code={code}
                setCode={setCode}
                onClickResend={handleResend}
                method={method}
                testId="request-sms_SmsCode_code"
              />
            </div>
            <div className="request-sms__form__inputs">
              <Col className="request-sms__form__button">
                <AdvancedButton
                  disabled={disableButton}
                  fullWidth
                  text={translate('COMPONENTS.ADVANCED_BUTTON.ENTER')}
                  onClick={handleValidateSMS}
                  testId="request-sms_SmsCode_Submit"
                />
              </Col>
              <Col>
                <AdvancedButton
                  variant="outlined"
                  fullWidth
                  text={translate('COMPONENTS.ADVANCED_BUTTON.RETURN')}
                  onClick={() => {
                    navigate.back();
                  }}
                />
              </Col>
            </div>
          </AdvancedForm>
        </div>
      </div>
    </>
  );
};

export default RequestSms;
